import { Alert } from '@/global/components/Alert'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import { Button } from '@/global/components/Button'
import {
    FormDatePicker,
    FormField,
    FormFieldSelect,
} from '@/global/components/Form'
import IconeAjuda from '@/global/components/IconeAjuda'
import { SelectFuncionario } from '@/global/components/Selects'
import { useCompetencia } from '@/global/components/Selects/SelectCompetencia'
import { cn } from '@/lib/utils'
import Grid from '@/global/components/ui/Grid'
import {
    CreateTrocaAutomaticaTabelaSchema,
    TabelaHorarioFuncionario,
    tabelaHorarios,
    TrocaAutomaticaTabela,
    trocaHorario,
} from '@/service/api'
import { useStore } from '@/store'
import CalendarioTabelasHorario from '@/templates/Cadastro/Funcionarios/organismos/forms/TabelaHorario/Calendario/CalendarioTabelasHorario'
import dayjs from 'dayjs'
import { RefreshCwIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import AccordionRepetir from '../components/accordion'

interface IProps {
    editar?: boolean
}

export default function FormTrocaAutomatica({ editar }: IProps) {
    const [empresaId, funcionarioId] = useStore(state => [
        state.companyId,
        state.employeeId,
    ])
    const { data = [] } = tabelaHorarios.useFetch({
        refetchOnWindowFocus: false,
    })

    const { watch, setValue, trigger } = useFormContext<TrocaAutomaticaTabela>()
    const { dataInicial, tabelaHorarioId, ...rest } = watch()
    const [mes, setMes] = useCompetencia(dayjs(dataInicial).format('YYYY-MM'))

    useEffect(() => {
        if (dataInicial) {
            setValue('repeticaoSemanalDia', dayjs.utc(dataInicial).day())
            setMes(dayjs(dataInicial).format('YYYY-MM'))
        }
    }, [dataInicial])

    const [tabelas, setTabelas] = useState<TabelaHorarioFuncionario[] | string>(
        [],
    )
    const [isRefetching, setIsRefetching] = useState(false)

    async function refetchData() {
        setIsRefetching(true)
        const postData = { dataInicial, tabelaHorarioId, ...rest }

        try {
            await trigger()
        } catch (e) {
            return setIsRefetching(false)
        }

        const body = CreateTrocaAutomaticaTabelaSchema.safeParse(postData)

        if (!body.success) return setIsRefetching(false)

        try {
            const res = await trocaHorario.trocaAutomatica.post<
                any,
                TabelaHorarioFuncionario[]
            >(`preview/${mes}`, {
                ...body.data,
                funcionarioId,
                empresaId,
            })
            setTabelas(res)
        } catch (e) {
            setTabelas(String((e as Error).message || 'Erro ao gerar tabelas'))
        } finally {
            setIsRefetching(false)
        }
    }

    useEffect(() => {
        setTabelas([])
    }, [mes])

    return (
        <Grid container>
            <Grid item xs={12}>
                <SelectFuncionario variant="form" disabled={editar} />
            </Grid>
            <Grid item xs={12}>
                <FormFieldSelect
                    label="Tabela de horário"
                    name="tabelaHorarioId"
                    required
                    options={data.map(tabela => ({
                        label: tabela.nome,
                        value: tabela.id.toString(),
                    }))}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormDatePicker
                    label="A partir de"
                    name="dataInicial"
                    type="date"
                    required
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormDatePicker
                    label="Até"
                    name="dataFinal"
                    type="date"
                    helperText="Deixe em branco para indeterminado"
                />
            </Grid>
            <Grid item xs={12}>
                <HorizontalDivider className="mt-2 mb-4 select-none">
                    A troca vai se repetir
                </HorizontalDivider>
                <AccordionRepetir />
            </Grid>
            <Grid item xs={3}>
                <FormField
                    label="A troca vai durar"
                    name="duracaoRepeticao"
                    type="number"
                    min={1}
                    max={999}
                    required
                />
            </Grid>
            <Grid item xs={3}>
                <FormFieldSelect
                    name="tipoDuracaoRepeticao"
                    options={[
                        {
                            label: 'Dia(s)',
                            value: 'DIARIO',
                        },
                        {
                            label: 'Semana(s)',
                            value: 'SEMANAL',
                        },
                        {
                            label: 'Mês(es)',
                            value: 'MENSAL',
                        },
                    ]}
                />
            </Grid>
            <Grid item xs={12}>
                <div className="mt-2 mb-4 flex select-none items-center gap-2 flex-nowrap">
                    <div className="h-px flex-1 bg-border" />
                    <span className="inline-flex items-center">
                        Prévia da troca automática
                        <IconeAjuda>
                            Esta prévia não salva as alterações nem reflete o
                            horário real do funcionário. É apenas uma
                            visualização de como ficará a troca automática de
                            horário no mês selecionado.
                        </IconeAjuda>
                    </span>
                    <Button
                        onClick={refetchData}
                        startIcon={
                            <RefreshCwIcon
                                className={cn(
                                    'size-4',
                                    isRefetching && 'animate-spin',
                                )}
                            />
                        }
                        className="ml-4"
                        variant="outlined"
                        disabled={isRefetching}
                    >
                        Atualizar {dayjs(mes, 'YYYY-MM').format('MMMM')}
                    </Button>
                    <div className="h-px flex-1 bg-border" />
                </div>
                {typeof tabelas === 'string' && (
                    <Alert severity="error" className="mb-4">
                        {tabelas}
                    </Alert>
                )}
                <CalendarioTabelasHorario
                    key={String(isRefetching)}
                    value={mes}
                    onChange={setMes}
                    tabelas={
                        Array.isArray(tabelas) && tabelas.length ? tabelas : []
                    }
                    editable={false}
                />
            </Grid>
        </Grid>
    )
}
